import type { OfferRequestSegment } from '@houzy/schemas/partner-cockpit/offer-requests';
import { useEffect, useMemo, useState } from 'react';

import { readSessionJson, writeSessionJson } from '../../../lib/sessionStore';
import { useTrackEvent } from '../../../lib/trackingEvents';
import type { OfferRequestsQueryParams } from '../api/offerRequestsListApi';
import { defaultOfferRequestsQuery } from '../api/useOfferRequestsList';
import { newlyUsedFilters } from '../filterTracking';
import { EMPTY_OFFER_REQUEST_FILTERS, OfferRequestSortKey, type OfferRequestFiltersValue } from '../types';

type PersistedView = {
    filters: OfferRequestFiltersValue;
    sort: OfferRequestSortKey;
    page: number;
    pageSize: number;
};

export type OfferRequestsView = PersistedView & {
    query: OfferRequestsQueryParams;
    changeFilters: (filters: OfferRequestFiltersValue) => void;
    changeSort: (sort: OfferRequestSortKey) => void;
    changePage: (page: number) => void;
    changePageSize: (pageSize: number) => void;
};

function pickStoredFilters(raw: unknown): OfferRequestFiltersValue {
    if (typeof raw !== 'object' || raw === null) {
        return EMPTY_OFFER_REQUEST_FILTERS;
    }
    const stored = raw as Record<string, unknown>;
    const known = Object.keys(EMPTY_OFFER_REQUEST_FILTERS) as (keyof OfferRequestFiltersValue)[];
    const picked = Object.fromEntries(
        known.flatMap((key) => {
            const value = stored[key];
            const expected = EMPTY_OFFER_REQUEST_FILTERS[key];
            const typeMatches = Array.isArray(expected) ? Array.isArray(value) : typeof value === typeof expected;
            return key in stored && typeMatches ? [[key, value]] : [];
        }),
    );
    return { ...EMPTY_OFFER_REQUEST_FILTERS, ...picked } as OfferRequestFiltersValue;
}

function readSessionView(sessionKey: string, segment: OfferRequestSegment): PersistedView {
    const fallbackPageSize = defaultOfferRequestsQuery(segment).pageSize;
    return readSessionJson(
        sessionKey,
        {
            filters: EMPTY_OFFER_REQUEST_FILTERS,
            sort: OfferRequestSortKey.Newest,
            page: 0,
            pageSize: fallbackPageSize,
        },
        (stored) => ({
            filters: pickStoredFilters(stored.filters),
            sort: stored.sort === OfferRequestSortKey.Oldest ? OfferRequestSortKey.Oldest : OfferRequestSortKey.Newest,
            page: typeof stored.page === 'number' && stored.page >= 0 ? stored.page : 0,
            pageSize: typeof stored.pageSize === 'number' && stored.pageSize > 0 ? stored.pageSize : fallbackPageSize,
        }),
    );
}

// Remembered for the session, so returning from a lead's detail lands the partner back where they were.
export function useOfferRequestsView(segment: OfferRequestSegment, sessionKey: string): OfferRequestsView {
    const initialView = useMemo(() => readSessionView(sessionKey, segment), [sessionKey, segment]);
    const [filters, setFilters] = useState<OfferRequestFiltersValue>(initialView.filters);
    const [sort, setSort] = useState<OfferRequestSortKey>(initialView.sort);
    const [page, setPage] = useState(initialView.page);
    const [pageSize, setPageSize] = useState(initialView.pageSize);
    const track = useTrackEvent();

    useEffect(() => {
        writeSessionJson(sessionKey, { filters, sort, page, pageSize });
    }, [sessionKey, filters, sort, page, pageSize]);

    return {
        filters,
        sort,
        page,
        pageSize,
        query: { segment, start: page * pageSize, pageSize, sort, filters },
        // Narrowing the result set can leave the current page past the end.
        changeFilters: (next) => {
            for (const filter of newlyUsedFilters(filters, next)) {
                track('Offer Request Filter Used', { filter });
            }
            setFilters(next);
            setPage(0);
        },
        changeSort: (next) => {
            setSort(next);
            setPage(0);
        },
        changePage: setPage,
        changePageSize: (next) => {
            setPageSize(next);
            setPage(0);
        },
    };
}

export function pageRange(view: OfferRequestsView, total: number, itemCount: number): { from: number; to: number } {
    const offset = view.page * view.pageSize;
    return {
        from: total === 0 ? 0 : offset + 1,
        to: Math.min(total, offset + itemCount),
    };
}
